/**
 * A szerkesztő témái: a játékban már kidolgozott öt helyszín.
 * A téma adja a szélességeket, a színpalettát, a kamerát és a díszletezési profilt.
 */
import type { AmbientPalette, TrackTheme } from '../core/types';

export interface ThemePreset {
  id: TrackTheme;
  label: string;
  blurb: string;
  /** a szerkesztő felületén használt kiemelőszín */
  accent: string;
  roadHalfWidth: number;
  kerbWidth: number;
  sidewalkWidth: number;
  ambient: AmbientPalette;
  cameraZoom: number;
}

export const THEMES: Record<TrackTheme, ThemePreset> = {
  city: {
    id: 'city',
    label: 'Város',
    blurb: 'Belvárosi házsor, villamos, parkos belső ív.',
    accent: '#ffc857',
    roadHalfWidth: 96,
    kerbWidth: 12,
    sidewalkWidth: 36,
    ambient: {
      sky: '#202838',
      asphalt: '#4b5059',
      asphaltDark: '#3d424b',
      lane: '#e7d6a2',
      kerb: '#c9543f',
      sidewalk: '#8f8c84',
      wall: '#2b3140',
      accent: '#ffc857',
      shadow: 'rgba(10,12,18,0.38)',
    },
    cameraZoom: 1,
  },
  lake: {
    id: 'lake',
    label: 'Tengerpart',
    blurb: 'Tó- és tengerpart: fák, padok, büfék a belső parton.',
    accent: '#7ed0c0',
    roadHalfWidth: 100,
    kerbWidth: 12,
    sidewalkWidth: 32,
    ambient: {
      sky: '#1d3a47',
      asphalt: '#4a4f58',
      asphaltDark: '#3c4149',
      lane: '#f0e2b0',
      kerb: '#c95a45',
      sidewalk: '#cbbd9a',
      wall: '#2f6470',
      accent: '#7ed0c0',
      shadow: 'rgba(8,14,18,0.4)',
    },
    cameraZoom: 0.95,
  },
  desert: {
    id: 'desert',
    label: 'Sivatag',
    blurb: 'Poros egyenesek, kaktuszok, kátyúk, bányatorony.',
    accent: '#e08a3c',
    roadHalfWidth: 100,
    kerbWidth: 12,
    sidewalkWidth: 30,
    ambient: {
      sky: '#d99a56',
      asphalt: '#5a5148',
      asphaltDark: '#4a423a',
      lane: '#f1dfb2',
      kerb: '#c05a3a',
      sidewalk: '#c9a877',
      wall: '#d6b078',
      accent: '#e08a3c',
      shadow: 'rgba(70,45,20,0.4)',
    },
    cameraZoom: 0.95,
  },
  circuit: {
    id: 'circuit',
    label: 'Versenypálya',
    blurb: 'Lelátók, gumifalak, pályafestés, füves belső.',
    accent: '#e10600',
    roadHalfWidth: 100,
    kerbWidth: 12,
    sidewalkWidth: 34,
    ambient: {
      sky: '#1b2430',
      asphalt: '#454a53',
      asphaltDark: '#383c44',
      lane: '#f5f0e6',
      kerb: '#d23b32',
      sidewalk: '#b8b6ae',
      wall: '#3a7448',
      accent: '#e10600',
      shadow: 'rgba(6,9,12,0.42)',
    },
    cameraZoom: 0.84,
  },
  suburb: {
    id: 'suburb',
    label: 'Külváros',
    blurb: 'Raktárnegyed, hosszú beton, ipari utcabútorok.',
    accent: '#ffb347',
    roadHalfWidth: 104,
    kerbWidth: 12,
    sidewalkWidth: 30,
    ambient: {
      sky: '#1d222b',
      asphalt: '#43474e',
      asphaltDark: '#363a41',
      lane: '#d9c98f',
      kerb: '#b9503c',
      sidewalk: '#8d8b85',
      wall: '#252a33',
      accent: '#ffb347',
      shadow: 'rgba(8,10,14,0.42)',
    },
    cameraZoom: 0.96,
  },
  snow: {
    id: 'snow',
    label: 'Havas',
    blurb: 'Hófúvás, jégpálya, fenyves.',
    accent: '#8fd3ff',
    roadHalfWidth: 100,
    kerbWidth: 12,
    sidewalkWidth: 32,
    ambient: {
      sky: '#cfe4f2',
      asphalt: '#4a5158',
      asphaltDark: '#3f454b',
      lane: '#f4fbff',
      kerb: '#5b8fc0',
      sidewalk: '#e4eef7',
      wall: '#c7dced',
      accent: '#7fd4ff',
      shadow: 'rgba(40,70,100,0.28)',
    },
    cameraZoom: 0.95,
  },
};

export const themeOf = (id: TrackTheme): ThemePreset => THEMES[id] ?? THEMES.city;
